// Every English string on the chef's Today screen and the plan's step strip
// (kitchen flow, Batch 4), in one place for a later Urdu table.
import type { NextStep, PlanStep, StatusLine } from './todayLogic'

const items = (n: number) => `${n} item${n === 1 ? '' : 's'}`

export const TT = {
  tabToday: 'Today',
  tabEvents: 'Events',
  tabRequests: 'Requests',
  todayTitle: 'Today',
  subtitle: (date: string) => `${date} · what needs the kitchen`,
  askForStock: 'Ask for stock',
  makeFood: 'Make food',
  more: 'More',
  back: 'Back',
  backToday: 'Today',
  // More menu.
  sendBack: 'Send back to store',
  count: 'Count the kitchen',
  throwAway: 'Throw away',
  basics: 'Basics',

  needsTitle: 'Needs you now',
  needsNone: 'Nothing needs you right now.',
  comingTitle: 'Coming up',
  comingNone: 'Nothing planned for the next 7 days.',
  allEvents: 'All events',
  loadFailed: 'Could not load this — the connection may be down.',
  retry: 'Try again',
  loading: 'Loading…',

  // Needs you now rows.
  arrivalTitle: 'Stock arrived from the store — check it in',
  arrivalFor: (plan: string, when: string) => (when ? `For ${plan} (${when})` : `For ${plan}`),
  arrivalReq: (no: string) => `Request ${no}`,
  sentAgo: (ago: string, who: string | null) => (who ? `sent ${ago} by ${who}` : `sent ${ago}`),
  checkIn: 'Check it in',
  shortTitle: (no: string) => `Stock went missing on the way — ${no}`,
  shortHelp: (n: number, plan: string | null) => `${items(n)} arrived short${plan ? ` for ${plan}` : ''}. Check it with the store.`,
  open: 'Open',
  overdueTitle: (name: string, days: number) =>
    `${name} finished ${days <= 1 ? 'yesterday' : `${days} days ago`} but was never wrapped up`,
  overdueHelp: 'Its food is still on the kitchen’s books. Say what was used, kept or thrown away.',
  wrapUp: 'Wrap up',
  basicsTitle: (n: number) => `${n} kitchen basic${n === 1 ? ' is' : 's are'} low`,
  basicsNames: (names: string[]) => (names.length <= 4 ? names.join(', ') : `${names.slice(0, 3).join(', ')} and ${names.length - 3} more`),
  askForThem: 'Ask for them',
  asking: 'Asking…',

  // Coming up rows.
  dayToday: 'Today',
  dayTomorrow: 'Tomorrow',
  guests: (n: number) => `${n} guest${n === 1 ? '' : 's'}`,
  portions: (n: number) => `${n} portion${n === 1 ? '' : 's'}`,
  dishes: (n: number) => `${n} dish${n === 1 ? '' : 'es'}`,
  served: 'served at the venue',
  packed: 'packed to go',

  steps: {
    planned: 'Planned',
    asked: 'Stock asked',
    here: 'Stock here',
    cooking: 'Cooking',
    wrapped: 'Wrapped up',
  } satisfies Record<PlanStep, string>,
  // The plan view's strip words step 2 and 5 as what to do.
  stepsPlan: {
    planned: 'Planned',
    asked: 'Ask the store',
    here: 'Stock here',
    cooking: 'Cooking',
    wrapped: 'Event done — wrap up',
  } satisfies Record<PlanStep, string>,
  stepDone: 'Done',
  stepNow: 'Now',
  stepsHelp: 'Steps: Planned → Stock asked → Stock here → Cooking → Wrapped up.',

  /** The one button a plan card shows. */
  nextButton: {
    confirm: 'Open',
    'send-to-store': 'Send to store',
    'check-in': 'Check it in',
    wait: 'Open',
    start: 'Start cooking',
    'wrap-up': 'Event done — wrap up',
  } satisfies Record<NextStep, string>,
  started: 'Cooking started',

  // The plan view's "Next" box.
  nextConfirm: 'Next: lock the plan',
  nextConfirmHelp: 'Check the dishes, then lock the plan in. The sheet below is worked out from today’s recipes.',
  nextSend: (ask: number) => (ask === 0 ? 'Next: send it on — the kitchen has everything' : `Next: ask the store for ${items(ask)}`),
  nextSendHelp: (have: number, cooking: boolean) =>
    [have > 0 ? `The kitchen already has enough of ${have} more.` : '', cooking ? 'Cooking has started — the store has not been asked yet.' : 'You can still change the dishes after this.']
      .filter(Boolean).join(' '),
  nextCheckIn: 'Next: check in what the store sent',
  nextCheckInHelp: 'Count what arrived so the kitchen’s stock is right.',
  nextWait: 'Next: wait for the store',
  nextWaitHelp: (line: string) => `${line}. You can start cooking with what is here.`,
  startAnyway: 'Start cooking anyway',
  nextStart: 'Next: start cooking',
  nextStartHelp: 'Everything the store is sending is in the kitchen.',
  nextWrap: 'Next: when the event is over, wrap up',
  nextWrapHelp: 'Say what was used, what is left, what goes back to the store and what was thrown away.',
  nextWrapLate: 'Next: wrap up — the event is over',
  history: (n: number) => `History (${n})`,
  showAll: (n: number) => `Show all ${n} items`,
  showFewer: 'Show fewer',
  haveIt: 'Have it',
  colNeed: 'Recipes need',
  colFree: 'Free in kitchen',
  colAsk: 'Ask the store',
  colAsked: 'Asked the store',
  colStore: 'Store has',
  colCost: 'Cost',
  changeDishes: 'Change dishes',
  change: 'Change',
  booking: (no: string) => `booking ${no}`,
  noRecipeWarn: (names: string[]) => {
    const list = names.join(', ')
    return names.length === 1
      ? `${list} has no recipe. Nothing for it is on the sheet below, so the store won’t be asked for its ingredients.`
      : `${list} have no recipe. Nothing for them is on the sheet below, so the store won’t be asked for their ingredients.`
  },
  addRecipe: 'Add recipe',
  askAnyway: 'Ask anyway',
}

/** "Asked the store · 5 of 9 items sent, 4 still to come". */
export function statusText(s: StatusLine): string {
  switch (s.kind) {
    case 'notLocked':
      return 'Planned · not locked yet'
    case 'notAsked':
      return s.cooking ? 'Cooking · the store hasn’t been asked yet' : 'Planned · the store hasn’t been asked yet'
    case 'nothingAsked':
      return 'Stock here · the kitchen had everything'
    case 'waiting':
      return s.total > 0 ? `Asked the store · waiting for ${items(s.total)}` : 'Asked the store · waiting for it to send'
    case 'partly':
      return `Asked the store · ${s.sent} of ${items(s.total)} sent, ${s.toCome} still to come`
    case 'arrived':
      return s.items ? `Stock arrived · ${items(s.items)} to check in` : 'Stock arrived · check it in'
    case 'here':
      return s.total != null ? `Stock here (${s.here} of ${items(s.total)})` : 'Stock here'
    case 'refused':
      return 'The store turned the request down'
    case 'cooking':
      return 'Cooking'
    case 'cooked':
      return 'All cooked · wrap up after the event'
  }
}

/** "just now", "10 min ago", "3 h ago", or the date. */
export function agoText(at: string | null, now: Date, fmtDate: (d: string) => string): string {
  if (!at) return ''
  const ms = now.getTime() - new Date(at).getTime()
  if (!Number.isFinite(ms)) return ''
  const min = Math.floor(ms / 60_000)
  if (min < 1) return 'just now'
  if (min < 60) return `${min} min ago`
  const h = Math.floor(min / 60)
  if (h < 24) return `${h} h ago`
  return `on ${fmtDate(at)}`
}
